import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DateEx } from '../../../functions/date-ex';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';

interface MonthDayItem {
  dateKey: string;
  date: Date;
  open: number;
  pastDue: number;
  completed: number;
  draft: number;
  active?: boolean;
}

interface MonthCell {
  date: Date;
  inRange: boolean;
  dateKey: string;
}

@Component({
  selector: 'app-task-months',
  imports: [CommonModule, ButtonComponent, LoadingPanelComponent],
  template: `
    <section
      class="tw-flex tw-items-center tw-justify-center tw-flex-col"
      aria-labelledby="task-months-range"
    >
      <div
        class="tw-flex tw-flex-wrap tw-items-center tw-justify-center sm:tw-justify-between tw-w-full tw-gap-2 tw-pb-4"
      >
        <div
          class="tw-flex tw-justify-center sm:tw-justify-start tw-min-w-[160px]"
        >
          <button
            type="button"
            class="tw-font-bold tw-underline tw-text-[var(--sed-link-color)] tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 tw-whitespace-nowrap focus:tw-outline focus:tw-outline-2 focus:tw-outline-offset-[3px]"
            (click)="goToCurrentThreeMonths()"
          >
            Return to current 3 months
          </button>
        </div>
        <span
          class="tw-inline-flex tw-items-center tw-justify-center tw-w-full sm:tw-w-auto tw-gap-1"
        >
          <app-button
            icon="fa-light fa-chevron-left"
            classes="calendar-button calendar-left"
            label=""
            ariaLabel="Previous month"
            title="Previous month"
            (click)="goToPrevious()"
          />
          <b id="task-months-range" class="date-span" aria-live="polite">{{
            rangeLabel
          }}</b>
          <app-button
            icon="fa-light fa-chevron-right"
            classes="calendar-button calendar-right"
            label=""
            ariaLabel="Next month"
            title="Next month"
            (click)="goToNext()"
          />
        </span>
        <div
          class="tw-min-w-[160px] tw-hidden sm:tw-block"
          aria-hidden="true"
        ></div>
      </div>
      <ul
        aria-label="Task status"
        class="tw-flex tw-w-full tw-gap-4 tw-flex-wrap tw-pb-4 tw-m-0 tw-list-none tw-p-0 tw-font-bold tw-text-[var(--sed-black-80-color)]"
      >
        <li class="tw-flex tw-items-center">
          <div
            class="tw-rounded-full tw-size-4 tw-bg-[var(--sed-status-blue-color)] tw-mr-2"
            aria-hidden="true"
          ></div>
          <div>Open</div>
        </li>
        <li class="tw-flex tw-items-center">
          <div
            class="tw-rounded-full tw-size-4 tw-bg-[var(--sed-status-red-color)] tw-mr-2"
            aria-hidden="true"
          ></div>
          <div>Past due</div>
        </li>
        @if (includeCompleted) {
          <li class="tw-flex tw-items-center">
            <div
              class="tw-rounded-full tw-size-4 tw-bg-[var(--sed-status-green-color)] tw-mr-2"
              aria-hidden="true"
            ></div>
            <div>Completed</div>
          </li>
        }
      </ul>
      <app-loading-panel
        loadingStyle="Box"
        [loadingHeight]="220"
        [state]="loading ? 'Loading' : 'Loaded'"
        class="tw-w-full"
      >
        <ng-template #loaded>
          <div
            class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4 tw-w-full"
          >
            @for (month of months; track month.key) {
              <section [attr.aria-labelledby]="'task-month-' + month.key">
                <h3
                  [id]="'task-month-' + month.key"
                  class="tw-text-left tw-font-bold tw-mb-2 tw-text-[var(--sed-dynamic-black-color)]"
                >
                  {{ month.label }}
                </h3>
                <div
                  class="tw-grid tw-grid-cols-7 tw-gap-1 tw-text-center tw-text-xs tw-font-bold tw-mb-1"
                >
                  @for (weekday of weekdays; track weekday.short) {
                    <div>
                      <span aria-hidden="true">{{ weekday.short }}</span>
                      <span class="tw-sr-only">{{ weekday.name }}</span>
                    </div>
                  }
                </div>
                @for (week of month.weeks; track week[0].dateKey) {
                  <div class="tw-grid tw-grid-cols-7 tw-gap-1 tw-mb-1">
                    @for (cell of week; track cell.dateKey) {
                      @if (cell.inRange) {
                        @let item = itemFor(cell);
                        <button
                          type="button"
                          class="tw-min-h-16 tw-rounded tw-border tw-border-[var(--sed-grey-60-color)] tw-bg-white tw-p-1 tw-text-left hover:tw-drop-shadow-[0_2px_2px_var(--sed-grey-100-color)] focus:tw-outline focus:tw-outline-2 focus:tw-outline-[var(--sed-engaged-black-color)] focus:tw-outline-offset-[2px]"
                          [attr.aria-label]="dayAriaLabel(cell)"
                          [attr.aria-pressed]="!!item?.active"
                          [class.tw-drop-shadow-[0_2px_2px_var(--sed-grey-60-color)]]="
                            !!item?.active
                          "
                          (click)="calendarOnClick(cell)"
                        >
                          <span class="tw-block tw-font-bold tw-text-sm">{{
                            cell.date.getDate()
                          }}</span>
                          @let status = statusFor(item);
                          @if (status) {
                            <span
                              class="tw-block tw-rounded-full tw-size-3 tw-mt-1"
                              [class.tw-bg-[var(--sed-status-blue-color)]]="
                                status === 'open'
                              "
                              [class.tw-bg-[var(--sed-status-red-color)]]="
                                status === 'pastDue'
                              "
                              [class.tw-bg-[var(--sed-status-green-color)]]="
                                status === 'completed'
                              "
                              aria-hidden="true"
                            ></span>
                          }
                        </button>
                      } @else {
                        <div class="tw-min-h-16" aria-hidden="true"></div>
                      }
                    }
                  </div>
                }
              </section>
            }
          </div>
        </ng-template>
      </app-loading-panel>
    </section>
  `,
  styles: `
    .date-span {
      font-size: 15px;
      color: black;
      min-width: 170px;
      display: inline-block;
      text-align: center;
      padding: 0 8px;
    }
  `,
})
export class TaskMonthsComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Output() calenderAction = new EventEmitter<{
    action: string;
    startDate: Date;
    endDate: Date;
  }>();

  @Input() items: MonthDayItem[] = [];

  @Input()
  loading = false;

  @Input() includeCompleted = false;

  protected weekdays = [
    { short: 'Mon', name: 'Monday' },
    { short: 'Tue', name: 'Tuesday' },
    { short: 'Wed', name: 'Wednesday' },
    { short: 'Thu', name: 'Thursday' },
    { short: 'Fri', name: 'Friday' },
    { short: 'Sat', name: 'Saturday' },
    { short: 'Sun', name: 'Sunday' },
  ];
  protected months: {
    key: string;
    label: string;
    weeks: MonthCell[][];
  }[] = [];
  protected rangeLabel = '';

  private rangeStart: Date;
  private itemsByDay = new Map<string, MonthDayItem>();

  ngOnInit() {
    this.setRange(this.currentWindowStart());
    this.emitRange('init');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['items']) {
      this.itemsByDay = new Map(
        (this.items ?? []).map((item) => [item.dateKey, item]),
      );
    }
  }

  protected itemFor(cell: MonthCell): MonthDayItem | undefined {
    return this.itemsByDay.get(cell.dateKey);
  }

  protected statusFor(
    item: MonthDayItem,
  ): 'open' | 'pastDue' | 'completed' | null {
    if (item?.pastDue) return 'pastDue';
    if (item?.open) return 'open';
    if (item?.completed) return 'completed';
    return null;
  }

  protected dayAriaLabel(cell: MonthCell): string {
    const item = this.itemFor(cell);
    const dateText = cell.date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const parts = [dateText];
    if (item?.open) {
      parts.push(`${item.open} open`);
    }
    if (item?.pastDue) {
      parts.push(`${item.pastDue} past due`);
    }
    if (item?.completed) {
      parts.push(`${item.completed} completed`);
    }
    return parts.join(', ');
  }

  protected calendarOnClick(cell: MonthCell) {
    this.calenderAction.emit({
      action: 'calendarClick',
      startDate: cell.date,
      endDate: cell.date,
    });
  }

  protected goToPrevious() {
    this.setRange(
      new Date(
        this.rangeStart.getFullYear(),
        this.rangeStart.getMonth() - 1,
        1,
      ),
    );
    this.emitRange('previous');
  }

  protected goToNext() {
    this.setRange(
      new Date(
        this.rangeStart.getFullYear(),
        this.rangeStart.getMonth() + 1,
        1,
      ),
    );
    this.emitRange('next');
  }

  protected goToCurrentThreeMonths() {
    this.setRange(this.currentWindowStart());
    this.emitRange('today');
  }

  private currentWindowStart(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() - 1, 1);
  }

  private emitRange(action: string) {
    const end = new Date(
      this.rangeStart.getFullYear(),
      this.rangeStart.getMonth() + 3,
      0,
    );
    this.calenderAction.emit({
      action,
      startDate: this.rangeStart,
      endDate: end,
    });
  }

  private setRange(firstOfWindow: Date) {
    this.rangeStart = new Date(
      firstOfWindow.getFullYear(),
      firstOfWindow.getMonth(),
      1,
    );
    this.months = [0, 1, 2].map((offset) => {
      const first = new Date(
        this.rangeStart.getFullYear(),
        this.rangeStart.getMonth() + offset,
        1,
      );
      return {
        key: `${first.getFullYear()}-${first.getMonth()}`,
        label: first.toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric',
        }),
        weeks: this.weeksForMonth(first),
      };
    });
    const last = new Date(
      this.rangeStart.getFullYear(),
      this.rangeStart.getMonth() + 2,
      1,
    );
    const startText = this.rangeStart.toLocaleDateString('en-US', {
      month: 'short',
      year:
        this.rangeStart.getFullYear() === last.getFullYear()
          ? undefined
          : 'numeric',
    });
    const endText = last.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
    this.rangeLabel = `${startText} – ${endText}`;
  }

  private weeksForMonth(firstOfMonth: Date): MonthCell[][] {
    const gridStart = DateEx.getStartOfWeek(firstOfMonth);
    const lastOfMonth = new Date(
      firstOfMonth.getFullYear(),
      firstOfMonth.getMonth() + 1,
      0,
    );
    const gridEnd = DateEx.getEndOfWeek(lastOfMonth);
    const weeks: MonthCell[][] = [];
    const cursor = new Date(
      gridStart.getFullYear(),
      gridStart.getMonth(),
      gridStart.getDate(),
    );
    while (cursor <= gridEnd) {
      const week: MonthCell[] = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(cursor);
        week.push({
          date,
          inRange:
            date.getFullYear() === firstOfMonth.getFullYear() &&
            date.getMonth() === firstOfMonth.getMonth(),
          dateKey: this.dateKey(date),
        });
        cursor.setDate(cursor.getDate() + 1);
      }
      weeks.push(week);
    }
    return weeks;
  }

  private dateKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }
}
